import { finishEditingCurrentElement } from '../../selection/selectionActions';
import { getCommentsInputMap } from '../store/commentsInputSelector';
import { Thunk } from '../../../types/reduxTypes';
import { asObject, ImMap, prop } from '../../../../common/utils/immutableHelper';
import { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import doesEditorJsonHaveText from '../../../../common/tiptap/utils/jsonContentUtils/doesEditorJsonHaveText';
import { getComment } from '../store/commentsSelector';
import globalLogger from '../../../logger';
import { isEqual } from 'lodash';
import { cancelEditingComment, stopEditingComment } from '../store/localCommentActions';
import { commentsAdd, commentsUpdate } from '../store/commentActions';
import { getCurrentUserId } from '../../../user/currentUserSelector';

const logger = globalLogger.createChannel('commentThreadCollapsedActions');

type CommentInputType = ImMap<{
    isEditing: boolean;
    text: string;
}>;

type CommentDetails = {
    elementId: string;
    threadId: string;
    commentId: string;
    text: TiptapContent;
};

/**
 * When the collapsed comment thread is closed, we want to finish editing the comment thread & save
 * any changes that have been made.
 */
export const finishEditingCollapsedCommentThread =
    ({ elementId, threadId }: { elementId: string; threadId: string }): Thunk =>
    (dispatch, getState) => {
        dispatch(finishEditingCurrentElement());

        const state = getState();
        const commentsInputMap = getCommentsInputMap(state);
        const currentUserId = getCurrentUserId(state);

        const commentsToAdd: CommentDetails[] = [];
        const commentsToUpdate: CommentDetails[] = [];
        const commentsToStopEditing: CommentDetails[] = [];
        const commentsToCancelEditing: CommentDetails[] = [];

        // Collect each of the comment inputs that are currently being edited, with their keys and text
        commentsInputMap.forEach((commentInput: CommentInputType, key: string) => {
            if (!prop('isEditing', commentInput)) return;

            const text = asObject(prop('text', commentInput)) as TiptapContent;

            // New comments won't have an ID yet, so the comment thread ID is used
            const isNewComment = key === threadId;

            const commentDetails = { elementId, threadId, commentId: key, text };

            if (isNewComment) {
                doesEditorJsonHaveText(text)
                    ? commentsToAdd.push(commentDetails)
                    : commentsToStopEditing.push(commentDetails);
                return;
            }

            // Existing comment
            // If the text is empty, we revert back to the original text (cancel editing)
            if (!doesEditorJsonHaveText(text)) {
                commentsToCancelEditing.push(commentDetails);
                return;
            }

            // Otherwise we need to check if the text has changed
            const originalCommentText = getComment(state, { _id: key })?.text;
            if (!originalCommentText) {
                logger.error('Failed to find comment text for updating', { commentId: key });
                return;
            }

            isEqual(originalCommentText, commentsToUpdate)
                ? // If the text is the same as the original, we just stop editing
                  commentsToStopEditing.push(commentDetails)
                : // Otherwise we update the comment
                  commentsToUpdate.push(commentDetails);
        });

        commentsToStopEditing.forEach(({ commentId }) => dispatch(stopEditingComment({ _id: commentId })));
        commentsToCancelEditing.forEach(({ commentId }) => dispatch(cancelEditingComment({ _id: commentId })));
        commentsToAdd.forEach(({ text, elementId, threadId }) => dispatch(commentsAdd({ text, elementId, threadId })));
        commentsToUpdate.forEach(({ text, commentId, elementId }) =>
            dispatch(
                commentsUpdate({ _id: commentId, text, elementId, userId: currentUserId, contentUpdate: undefined }),
            ),
        );
    };
